import React, { useEffect, useMemo } from 'react';
import { MapPin, Tag } from 'lucide-react';
import { US_STATES } from '../../constants/usStates';

interface ThemeStyles {
  inlineStyles?: {
    buttonPrimary?: React.CSSProperties;
  };
}

export type ServiceAddress = {
  address: string;
  city: string;
  state: string;
  zip: string;
  locationNotes?: string;
  locationLabel?: string;
};

interface BookingStep4ServiceAddressProps {
  serviceAddress: ServiceAddress;
  onServiceAddressChange: (next: ServiceAddress) => void;
  rememberServiceAddress: boolean;
  onRememberServiceAddressChange: (next: boolean) => void;
  preferredServiceAddress?: ServiceAddress | null;
  themeStyles?: ThemeStyles;
  /** When false, hides "Remember this location for next time" (e.g. business booking flow). Default true. */
  showRememberOption?: boolean;
}

const BookingStep4ServiceAddress: React.FC<BookingStep4ServiceAddressProps> = ({
  serviceAddress,
  onServiceAddressChange,
  rememberServiceAddress,
  onRememberServiceAddressChange,
  preferredServiceAddress,
  themeStyles,
  showRememberOption = true
}) => {
  const primaryColor = themeStyles?.inlineStyles?.buttonPrimary?.backgroundColor || '#3B82F6';

  const isEmpty = useMemo(() => {
    const a = serviceAddress;
    return !(a.address || a.city || a.state || a.zip || a.locationNotes || a.locationLabel);
  }, [serviceAddress]);

  // If user has a preferred address and form is empty, prefill (non-destructive)
  useEffect(() => {
    if (!preferredServiceAddress) return;
    if (!isEmpty) return;
    onServiceAddressChange({
      address: preferredServiceAddress.address || '',
      city: preferredServiceAddress.city || '',
      state: preferredServiceAddress.state || '',
      zip: preferredServiceAddress.zip || '',
      locationNotes: preferredServiceAddress.locationNotes || '',
      locationLabel: preferredServiceAddress.locationLabel || ''
    });
    // If the address was prefilled from the user's preferred address, default "remember" to checked.
    if (!rememberServiceAddress) {
      onRememberServiceAddressChange(true);
    }
  }, [preferredServiceAddress, isEmpty, onServiceAddressChange, rememberServiceAddress, onRememberServiceAddressChange]);

  const updateField = (field: keyof ServiceAddress, value: string) => {
    onServiceAddressChange({
      ...serviceAddress,
      [field]: value
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <MapPin className="w-4 h-4" style={{ color: primaryColor }} />
          </div>
          <h2 className="text-xl text-gray-900">Enter Service Address</h2>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-5">
        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Address <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={serviceAddress.address}
            onChange={(e) => updateField('address', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* City */}
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={serviceAddress.city}
              onChange={(e) => updateField('city', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
            />
          </div>

          {/* State */}
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              State <span className="text-red-500">*</span>
            </label>
            <select
              value={serviceAddress.state}
              onChange={(e) => updateField('state', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white"
            >
              <option value="">Select a state</option>
              {US_STATES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* ZIP */}
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ZIP <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={serviceAddress.zip}
              onChange={(e) => updateField('zip', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
              inputMode="numeric"
            />
          </div>
        </div>

        {/* Location Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location Notes <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            value={serviceAddress.locationNotes || ''}
            onChange={(e) => updateField('locationNotes', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
            placeholder="Gate code, parking info, apt/unit, etc."
          />
        </div>

        {/* Remember (hidden when showRememberOption is false, e.g. business booking flow) */}
        {showRememberOption && (
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={rememberServiceAddress}
              onChange={(e) => onRememberServiceAddressChange(e.target.checked)}
              className="mt-1 h-4 w-4 text-[#0B4778] focus:ring-[#0B4778] border-gray-300 rounded"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">Remember this location for next time</div>
              <div className="text-xs text-gray-500 mt-0.5">
                If enabled, we’ll keep it as your last used location. You can also name it (optional).
              </div>
            </div>
          </div>
        )}

        {/* Location Label (disabled for now) */}
        {/* <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location label <span className="text-gray-400">(optional)</span>
          </label>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center text-gray-400">
              <Tag className="w-4 h-4" />
            </div>
            <input
              type="text"
              value={serviceAddress.locationLabel || ''}
              onChange={(e) => updateField('locationLabel', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
              placeholder="Home, Office, etc."
            />
          </div>
          <div className="text-xs text-gray-500 mt-2">Leave blank to save it only as Last used.</div>
        </div> */}

        <div className="text-xs text-gray-500 flex items-start gap-2">
          <span className="mt-0.5">ⓘ</span>
          <span>Your address is used only to fulfill your appointment and (optionally) for faster booking next time.</span>
        </div>
      </div>
    </div>
  );
};

export default BookingStep4ServiceAddress;

